import React, { FC, useState, useRef, useMemo, useEffect } from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import { v4 as uuidv4 } from 'uuid';
import { Link } from 'react-router-dom';
import { makeStyles } from 'tss-react/mui';
import { Record as ImmutableRecord } from 'immutable';
import { usePrevious, useUnmount, useMount } from 'react-use';

import List from '@mui/material/List';
import Paper from '@mui/material/Paper';
import Divider from '@mui/material/Divider';
import ListItem from '@mui/material/ListItem';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import ButtonBase from '@mui/material/ButtonBase';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import FormControlLabel from '@mui/material/FormControlLabel';

import LinkIcon from '@mui/icons-material/LinkRounded';
import RepeatIcon from '@mui/icons-material/RepeatRounded';
import TransferIcon from '@mui/icons-material/ReplyRounded';
import LinkOffIcon from '@mui/icons-material/LinkOffRounded';
import MessageIcon from '@mui/icons-material/LocalOfferRounded';
import AssignmentIcon from '@mui/icons-material/AssignmentRounded';
import NavigateNextIcon from '@mui/icons-material/NavigateNextRounded';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverRounded';

import { getLogger } from 'companion-app-components/utils/core';
import { transactionsUtils, transactionsTypes } from 'companion-app-components/flux/transactions';
import { scheduledTransactionsUtils } from 'companion-app-components/flux/scheduled-transactions';
import { isEntityShareUser } from 'companion-app-components/flux/account-groups/accountGroupsSelectors';

import QPopper from 'components/QPopper';
import QSwitch from 'components/QSwitch';
import UIState from 'components/UIState';
import useQDialogs from 'components/QDialogs/useQDialogs';
import TagsField from 'components/QuickenControls/TagsField';
import QTypography from 'components/MUIWrappers/QTypography';
import AddAndBrowseDocuments from 'components/AddAndBrowseDocuments';
import SelectScheduledTransactionDialog from 'components/Dialogs/SelectScheduledTransactionDialog';

import * as transactionsSelectors from 'data/transactions/selectors';
import { 
  getMatchState, 
  isSupportedTransferTxn, 
  isExistingTxn, 
  getTxnDifferences, 
} from 'data/transactions/utils';

import compose from 'utils/compose';

import checkNumIcon from 'assets/checknum_details.svg';

import RecurrenceAssigningDialog from './RecurrenceAssigningDialog';
import CreateNewRecurringDialog from './CreateNewRecurringDialog';
import DeleteRecurringDialog from './DeleteRecurringDialog';
import { useSelectOrDispatch } from './useSelectOrDispatch';
import { styles } from './styles';

const log = getLogger('component/DetailsPanel/index.tsx');

const useStyles = makeStyles()(styles as Record<string, any>);

interface DetailsPanelProps {
  editable: boolean;
  firstTabName: string;
  lastTabName: string;
  uiState: Record<string, any>;
  txForm: transactionsTypes.CashFlowTransaction;
  onSizeChange: () => void;
  unMatchAction?: () => void;
  goToTransferAction?: () => void;
  setUIState: (state: Record<string, any>) => void;
  onChange: (state: Record<string, any>, forceSave: boolean) => void;
}

const DetailsPanel: FC<DetailsPanelProps> = (props) => {

  const { classes } = useStyles();
  const { dialogAlert } = useQDialogs();

  const [memo, setMemo] = useState(props.txForm.memo);
  const [tags, setTags] = useState(props.txForm.tags);
  const [check, setCheck] = useState(props.txForm.check && props.txForm.check.number);
  const [sizesChanged, setSizesChanged] = useState(false);
  const [attachments, setAttachments] = useState(props.txForm.attachments);
  const [recurrencePayee, setRecurrencePayee] = useState(props.uiState.recurrencePayee);
  const [recurrenceFrequency, setRecurrenceFrequency] = useState(props.uiState.recurrenceFrequency);
  const [showSetupRecurrence, setShowSetupRecurrence] = useState(false);
  const [showNewRecurrenceProgress, setShowNewRecurrenceProgress] = useState(Boolean(props.uiState.newScheduledTransactionClientId));
  const [showDeleteRecurrenceProgress, setShowDeleteRecurrenceProgress] = useState(Boolean(props.uiState.deleteScheduledTransactionId));
  const [showUnlinkRecurrence, setShowUnlinkRecurrence] = useState(false);
  const [showSelectScheduledTransactionDialog, setShowSelectScheduledTransactionDialog] = useState(false);
  const [showAssignRecurrenceProgress, setShowAssignRecurrenceProgress] = useState(false);
  const [selectedScheduledTransactionId, setSelectedScheduledTransactionId] = useState<string | undefined>(undefined);
  const [callScheduledEditLightDialog, setCallScheduledEditLightDialog] = useState(false);
  const isEntityShareEnableUser = useSelector(isEntityShareUser);

  const setupRecurrenceRef = useRef(null);
  const createNewScheduledTxnTimeout = useRef<NodeJS.Timeout | null>(null);
  const isMountedRef = useRef(false);

  const { 
    scheduledTransactionsEnabled,
    scheduledTransactions,
    scheduledTransaction,
    scheduledTransactionsError,
    transactionsError,
    transactionDetailsComprehensive,
    showTxnJson,
    coaBucket,
    txnsByAccountId,
    accountIds,
    dispatchDeleteScheduledTransactionAction,
    dispatchCreateBillScoutScheduleAction,
    dispatchPerformTransactionAction,
    dispatchScheduledEditLightDialog,
  } = useSelectOrDispatch(props);

  const prevTnxForm = useRef(props.txForm);
  const prevScheduledTransactionsError = usePrevious(scheduledTransactionsError);
  const prevTransactionsError = usePrevious(transactionsError);

  const setInitialFocus = () => {
    const node = document.querySelectorAll('[name="details-field-notes"]');
    if (node && node.length > 0) {
      // @ts-expect-error: Property 'focus' does not exist on type 'Element'.
      node[0].focus();
    } else {
      setTimeout(() => setInitialFocus(), 100);
    }
  };

  useMount(() => {
    isMountedRef.current = true;
    setInitialFocus();
    return () => {
      isMountedRef.current = false;
    };
  });

  useEffect(() => {
    if (prevTnxForm?.current.id !== props.txForm.id) {
      setMemo(props.txForm.memo);
      setTags(props.txForm.tags);
      setCheck(props.txForm.check && props.txForm.check.number);
      setSizesChanged(false);
      setAttachments(props.txForm.attachments);
      setShowNewRecurrenceProgress(Boolean(props.uiState.newScheduledTransactionClientId));
      setShowDeleteRecurrenceProgress(Boolean(props.uiState.deleteScheduledTransactionId));
      setRecurrencePayee(props.uiState.recurrencePayee);
      setRecurrenceFrequency(props.uiState.recurrenceFrequency);
      prevTnxForm.current = props.txForm;
    }
  }, [prevTnxForm, props]);

  useEffect(() => {
    if (prevTnxForm?.current.id !== props.txForm.id) {
      return;
    }
    
    const changedProps = getTxnDifferences(prevTnxForm?.current, props.txForm);  
    if (changedProps.includes('memo')) {
      setMemo(props.txForm.memo);
    }
    if (changedProps.includes('tags')) {
      setTags(props.txForm.tags);
    }
    if (changedProps.includes('check')) {
      setCheck(props.txForm.check?.number);
    }

    prevTnxForm.current = props.txForm;
  }, [props, prevTnxForm]);

  useEffect(() => {

    if (sizesChanged) {
      setSizesChanged(false);
      props.onSizeChange?.();
    }

    if (showNewRecurrenceProgress) {
      if (scheduledTransaction?.id && props.uiState.newScheduledTransactionClientId
        && props.uiState.newScheduledTransactionClientId === scheduledTransaction.clientId
        && props.txForm.stModelId === scheduledTransaction.id
      ) {
        setShowNewRecurrenceProgress(false);
        setCallScheduledEditLightDialog(true);
        props.setUIState({ newScheduledTransactionClientId: undefined });
      } else if (!prevScheduledTransactionsError && scheduledTransactionsError) {
        setShowNewRecurrenceProgress(false);
        props.setUIState({ newScheduledTransactionClientId: undefined });
        dialogAlert('Sorry', "Can't create recurrence. Try again later.", undefined, ['ok']);
      }
    }

    if (showAssignRecurrenceProgress
      && ((!prevTransactionsError && transactionsError)
        || (props.txForm.stModelId && props.txForm.stModelId === selectedScheduledTransactionId))
    ) {
      setShowAssignRecurrenceProgress(false);
      setSelectedScheduledTransactionId(undefined);
    }

    if (showDeleteRecurrenceProgress
      && ((!prevScheduledTransactionsError && scheduledTransactionsError)
        || !props.txForm.stModelId)
    ) {
      setShowDeleteRecurrenceProgress(false);
      props.setUIState({
        deleteScheduledTransactionId: undefined,
        recurrencePayee: undefined,
        recurrenceFrequency: undefined,
      });
    }

  }, [props, scheduledTransactionsError, prevScheduledTransactionsError, 
    scheduledTransaction, showNewRecurrenceProgress, sizesChanged,
    showAssignRecurrenceProgress, prevTransactionsError, transactionsError,
    selectedScheduledTransactionId, showDeleteRecurrenceProgress, dialogAlert,
  ]);

  useEffect(() => {
    if (callScheduledEditLightDialog) {
      dispatchScheduledEditLightDialog(scheduledTransaction);
      setCallScheduledEditLightDialog(false);
    }
  }, [callScheduledEditLightDialog, dispatchScheduledEditLightDialog, scheduledTransaction]);

  useUnmount(() => {
    isMountedRef.current = false;
    if (createNewScheduledTxnTimeout.current) {
      clearTimeout(createNewScheduledTxnTimeout.current);
      createNewScheduledTxnTimeout.current = null;
    }
  });
  
  const hasFoundMatchingTransfer = useMemo(() => {
    if (transactionsUtils.isSplitTxn(props.txForm)) {
      return true;
    }
    const txns = txnsByAccountId.get(props.txForm.coa?.id);
    return Boolean(transactionsUtils.findMatchingTransferTx(props.txForm, txns));
  }, [props.txForm, txnsByAccountId]);

  let lastTab = 'addDoc';
  if (props.txForm.cpData) {
    lastTab = (getMatchState(props.txForm) === 'matched' && props.unMatchAction) ? 'unmatch' : 'addDoc';
  }
  if (isSupportedTransferTxn(props.txForm) && props.goToTransferAction) {
    lastTab = 'transfer';
  }

  const showSwitchProgress = showSetupRecurrence || showUnlinkRecurrence || showSelectScheduledTransactionDialog
    || showNewRecurrenceProgress || showAssignRecurrenceProgress || showDeleteRecurrenceProgress;

  const callOnChange = (updatedValues, forceSave = false) => {
    if (props.onChange) {
      props.onChange({ 
        memo,
        tags,
        sizesChanged,
        attachments,
        isBill: props.txForm.isBill,
        showNewRecurrenceProgress,
        showDeleteRecurrenceProgress,
        recurrencePayee,
        recurrenceFrequency,
        check: { number: check },
        ...updatedValues,
      }, forceSave);
    }
  };  

  const handleChange = (e, field) => {
    let fieldValue = e.target.value;
    if (field === 'memo') {
      setMemo(fieldValue);
    } else if (field === 'check') {
      if (fieldValue === null) {
        fieldValue = '';
      }
      setCheck(fieldValue);
    }
    callOnChange({
      memo: field === 'memo' ? fieldValue : memo,
      check: { number: field === 'check' ? fieldValue : check },
    });
  };

  const onFocus = (e) => {
    e.target.select();
  };

  const tagsChange = (tagSet) => {
    log.log('TAG CHANGE RECEIVED ', tagSet);
    setTags(tagSet);
    setSizesChanged(true);
    callOnChange({
      tags: tagSet,
      sizesChanged: true,
    });
  };

  const getRecurrenceLabel = () => {
    let recurrenceLabel;
    switch (coaBucket) {
      case transactionsSelectors.TransactionBucketEnum.EXPENSE:
        recurrenceLabel = props.txForm.stModelId ? 'Is recurring bill' : 'Is a bill';
        break;
      case transactionsSelectors.TransactionBucketEnum.INCOME:
        recurrenceLabel = 'Is recurring income';
        break;
      case transactionsSelectors.TransactionBucketEnum.TRANSFER:
        recurrenceLabel = 'Is recurring transfer';
        break;
      default:
        log.warn('Unexpected coa bucket = ', coaBucket);
        break;
    }
    return recurrenceLabel;
  };

  const createNewScheduledTransaction = () => {
    if (props.txForm.payee) {
      const newScheduledTransactionClientId = uuidv4().toUpperCase();
      props.setUIState({ newScheduledTransactionClientId });
      dispatchCreateBillScoutScheduleAction({
        clientId: newScheduledTransactionClientId,
        id: props.txForm.id,
      });
      setShowNewRecurrenceProgress(true);
      createNewScheduledTxnTimeout.current = setTimeout(() => {
        if (showNewRecurrenceProgress && newScheduledTransactionClientId === props.uiState.newScheduledTransactionClientId) {
          props.setUIState({ newScheduledTransactionClientId: undefined });
          if (showNewRecurrenceProgress) {
            setShowNewRecurrenceProgress(false);
          }
          dialogAlert('Sorry', "Can't create recurrence timeout. Try again later.", undefined, ['ok']);
        }
      }, 12000);
    } else {
      setShowNewRecurrenceProgress(true);
      dialogAlert('Sorry', 'Without payee name scheduled transactions cannot be created', () => {
        setShowNewRecurrenceProgress(false);
      }, ['ok']);
    }
  };

  const deleteScheduledTransaction = () => {
    if (assert(scheduledTransaction?.id, 'no scheduled transaction model id', scheduledTransaction?.id)) {
      const recurrencePayeeClone = scheduledTransaction?.transaction?.payee;
      const recurrenceFrequencyClone = scheduledTransactionsUtils.frequencyAliasFromRecurrence(scheduledTransaction?.recurrence);
      props.setUIState({
        deleteScheduledTransactionId: scheduledTransaction?.id,
        recurrencePayee: recurrencePayeeClone,
        recurrenceFrequency: recurrenceFrequencyClone,
      });
      dispatchDeleteScheduledTransactionAction({ id: scheduledTransaction?.id, isDeleted: true });
      setRecurrencePayee(recurrencePayeeClone);
      setRecurrenceFrequency(recurrenceFrequencyClone);
      setShowDeleteRecurrenceProgress(true);
      setTimeout(() => {
        if (showDeleteRecurrenceProgress
          && scheduledTransaction?.id === props.uiState.deleteScheduledTransactionId) {
          props.setUIState({
            recurrencePayee: undefined,
            recurrenceFrequency: undefined,
          });
          if (isMountedRef.current) {
            setShowDeleteRecurrenceProgress(false);
          }
          dialogAlert('Sorry', "Can't delete recurrence. Try again later.", undefined, ['ok']);
        }
      }, 8000);
    }
  };

  const getNewRecurrenceLabel = () => {
    let newRecurrenceLabel;
    switch (coaBucket) {
      case transactionsSelectors.TransactionBucketEnum.EXPENSE:
        newRecurrenceLabel = 'New recurring bill';
        break;
      case transactionsSelectors.TransactionBucketEnum.INCOME:
        newRecurrenceLabel = 'New recurring income';
        break;
      case transactionsSelectors.TransactionBucketEnum.TRANSFER:
        newRecurrenceLabel = 'New recurring transfer';
        break;
      default:
        log.warn('Unexpected coa bucket = ', coaBucket);
        break;
    }
    return newRecurrenceLabel;
  };

  const getAssignRecurrenceLabel = () => {
    let assignRecurrenceLabel;
    switch (coaBucket) {
      case transactionsSelectors.TransactionBucketEnum.EXPENSE:
        assignRecurrenceLabel = 'Assign to existing bill';
        break;
      case transactionsSelectors.TransactionBucketEnum.INCOME:
        assignRecurrenceLabel = 'Assign to existing income';
        break;
      case transactionsSelectors.TransactionBucketEnum.TRANSFER:
        assignRecurrenceLabel = 'Assign to existing transfer';
        break;
      default:
        log.warn('Unexpected coa bucket = ', coaBucket);
        break;
    }
    return assignRecurrenceLabel;
  };

  const getDeleteRecurrenceLabel = () => {
    let newRecurrenceLabel;
    switch (coaBucket) {
      case transactionsSelectors.TransactionBucketEnum.EXPENSE:
        newRecurrenceLabel = 'Delete recurring bill';
        break;
      case transactionsSelectors.TransactionBucketEnum.INCOME:
        newRecurrenceLabel = 'Delete recurring income';
        break;
      case transactionsSelectors.TransactionBucketEnum.TRANSFER:
        newRecurrenceLabel = 'Delete recurring transfer';
        break;
      default:
        log.warn('Unexpected coa bucket = ', coaBucket);
        break;
    }
    return newRecurrenceLabel;
  };

  const linkToScheduledTransaction = (selectedScheduledTransaction) => {
    if (assert(props.txForm.id && selectedScheduledTransaction.id, 'transaction id=%s or scheduled transaction id=%s missing', props.txForm.id, selectedScheduledTransaction.id)) {
      setShowSelectScheduledTransactionDialog(false);
      setShowAssignRecurrenceProgress(true);
      setSelectedScheduledTransactionId(selectedScheduledTransaction.id);
      setRecurrencePayee(selectedScheduledTransaction.transaction.payee);
      setRecurrenceFrequency(scheduledTransactionsUtils.frequencyAliasFromRecurrence(selectedScheduledTransaction.recurrence));
      dispatchPerformTransactionAction({ action: 'link-to-scheduled-txn', id: props.txForm.id, otherId: selectedScheduledTransaction.id },
        { undo: { userMessage: 'Transaction linked.' } });
      setTimeout(() => {
        if (showAssignRecurrenceProgress && selectedScheduledTransaction.id === selectedScheduledTransactionId) {
          if (isMountedRef.current) {
            setShowAssignRecurrenceProgress(false);
          }
          dialogAlert('Sorry', "Can't link to recurrence. Try again later.", undefined, ['ok']);
        }
      }, 8000);
    }
  };

  const documentsChange = (assets, forceSave) => {
    log.log('SAVING ATTACHMENTS ');
    setAttachments(assets);
    setSizesChanged(true);
    setTimeout(() => {
      callOnChange({
        attachments: assets,
        sizesChanged: true,
      }, forceSave);
    }, 2000);
  };


  const isPendingTxns = props.txForm.source === transactionsTypes.TransactionSource.ScheduledTransactionPending;
  const isTransferAccountPresent = accountIds.get(props.txForm.coa?.id as string);

  return (
    <Paper
      elevation={0}
      className={classes.detailsContainer}
    >
      {transactionDetailsComprehensive &&
        <div className={classes.webFirstContainer}>
          {/* **** MEMO/NOTES FIELD **** */}
          <div className={classes.detailsRow}>
            <div className={classes.rowIcon}>
              <AssignmentIcon fontSize="small" />
            </div>
            <div className={classes.fieldContainer}>
              <TextField
                id="details-notes-field"
                className={classes.textField}
                sx={{ mt: 4 }}
                autoFocus
                onChange={(e) => handleChange(e, 'memo')}
                placeholder="Memo"
                value={memo || ''}
                inputProps={{ onFocus, name: props.firstTabName }}
                disabled={!props.editable}
              />
            </div>
          </div>

          {/* **** TAGS FIELD (when no splits) **** */}
          {!transactionsUtils.isSplitTxn(props.txForm) &&
          <div className={classes.detailsRow}>
            <div className={classes.rowIcon}>
              <MessageIcon fontSize="small" />
            </div>
            <div className={classNames(classes.fieldContainer, classes.tagsFieldContainer)}>
              <TagsField
                className={classes.textField}
                value={tags}
                onChange={tagsChange}
                id="details-tags-field"
                editable={props.editable}
              />
            </div>
          </div>}

          {/* **** CHECK NUMBER FIELD **** */}
          <div className={classNames(classes.detailsRow, classes.checkRow)}>
            <div className={classes.rowIcon}>
              <img
                alt="Check Number"
                className={classes.checkNumber}
                src={checkNumIcon}
              />
            </div>
            <div className={classes.fieldContainer}>
              <TextField
                id="details-check-field"
                className={classes.textField}
                onChange={(e) => handleChange(e, 'check')}
                placeholder="Check Number"
                value={check || ''}
                inputProps={{ onFocus }}
                disabled={!props.editable}
              />
            </div>
          </div>
        </div>}

      {/* **** BILL SWITCH FIELD **** */}
      {scheduledTransactionsEnabled && isExistingTxn(props.txForm) && !isEntityShareEnableUser &&
      <div className={classes.group}>
        <div className={classes.contentRow}>

          {scheduledTransaction && scheduledTransaction.id ?
            <Link
              to={`/bills-and-income?selectNode=${scheduledTransaction && scheduledTransaction.id}`}
              className={classes.linkStyle}
            >
              <QTypography variant="body1">
                {getRecurrenceLabel()}
              </QTypography>
            </Link>
            :
            <QTypography variant="body1">
              {getRecurrenceLabel()}
            </QTypography>}

          <FormControlLabel
            checked={Boolean(scheduledTransaction)}
            labelPlacement="start"
            control={
              <QSwitch
                id="is-recurring-toggle"
                color="primary"
                ref={setupRecurrenceRef}
                showCheckProgress={showSwitchProgress}
                showUncheckProgress={showSwitchProgress}
              />
            }
            label=""
            onChange={(event) => {
              // @ts-expect-error: Property 'checked' does not exist on type 'EventTarget'.
              if (event.target.checked) {
                createNewScheduledTransaction();
              } else {
                deleteScheduledTransaction();
              }
            }}
          />
        </div>

        <QPopper
          anchorEl={setupRecurrenceRef.current}
          open={showSetupRecurrence}
          onClickAway={() => setShowSetupRecurrence((false))}
          placement="bottom-start"
          className={classes.recurringMenuPaper}
        >
          <List
            component="nav"
            aria-label="setup recurring options"
            className={classes.billMenu}
          >
            <ListItem
              id="create-new-recurring-button"
              button
              onClick={() => {
                createNewScheduledTransaction();
                setShowSetupRecurrence((false));
              }}
              className={classes.billMenuItem}
            >
              <ListItemIcon>
                <RepeatIcon />
              </ListItemIcon>
              <ListItemText primary={getNewRecurrenceLabel()} />
            </ListItem>

            {scheduledTransactions && scheduledTransactions.size > 0 &&
            <>
              <Divider />

              <ListItem
                id="assign-to-existing-button"
                button
                onClick={() => {
                  setShowSetupRecurrence(false);
                  setShowSelectScheduledTransactionDialog(true);
                }}
                className={classes.billMenuItem}
              >
                <ListItemIcon>
                  <LinkIcon />
                </ListItemIcon>
                <ListItemText primary={getAssignRecurrenceLabel()} />
                <NavigateNextIcon />
              </ListItem>
            </>}

          </List>
        </QPopper>

        {/* create new recurrence dialog */}
        <CreateNewRecurringDialog 
          showNewRecurrenceProgress={showNewRecurrenceProgress}
          uiState={props.uiState}
          txForm={props.txForm}
        />

        <QPopper
          anchorEl={setupRecurrenceRef.current}
          open={showUnlinkRecurrence}
          onClickAway={() => setShowUnlinkRecurrence(false)}
          placement="bottom-start"
        >
          <List
            component="nav"
            aria-label="unlink recurring options"
            sx={{ p: 0 }}
          >
            <ListItem
              id="remove-from-transaction-button"
              style={{ display: isPendingTxns ? 'none' : undefined }}
              button
              disabled={!showUnlinkRecurrence}
              onClick={() => {
                if (assert(props.txForm.id, 'remove from recurrence: no transaction id')) {
                  dispatchPerformTransactionAction({ action: 'unlink-to-scheduled-txn', id: props.txForm.id }, { undo: { userMessage: 'Transaction unlinked.' } });
                  setShowUnlinkRecurrence(false);
                  setSelectedScheduledTransactionId(undefined);
                }
              }}
            >
              <ListItemIcon>
                <LinkOffIcon />
              </ListItemIcon>
              <ListItemText primary="Unlink from recurring series" />
            </ListItem>

            <Divider
              style={{ display: isPendingTxns ? 'none' : undefined }}
            />

            <ListItem
              id="delete-recurring-transaction-button"
              button
              onClick={() => {
                deleteScheduledTransaction();
                setShowUnlinkRecurrence(false);
              }}
            >
              <ListItemIcon>
                <DeleteForeverOutlinedIcon />
              </ListItemIcon>
              <ListItemText primary={getDeleteRecurrenceLabel()} />
            </ListItem>
          </List>
        </QPopper>

        <SelectScheduledTransactionDialog
          open={showSelectScheduledTransactionDialog}
          onClose={() => setShowSelectScheduledTransactionDialog(false)}
          onSelected={linkToScheduledTransaction}
          maxWidth="md"
          classes={{ paper: classes.dialogPaper }}
        />

        {/* Recurrence Assigning dialog */}
        <RecurrenceAssigningDialog 
          showAssignRecurrenceProgress={showAssignRecurrenceProgress}
          txForm={props.txForm}
          recurrencePayee={recurrencePayee}
          recurrenceFrequency={recurrenceFrequency}
        />

        {/* Delete dialog */}
        <DeleteRecurringDialog 
          showDeleteRecurrenceProgress={showDeleteRecurrenceProgress}
          recurrencePayee={recurrencePayee}
          recurrenceFrequency={recurrenceFrequency}
        />

      </div>}

      {/* **** ADD DOCUMENT **** */}
      <AddAndBrowseDocuments
        id="attachments-upload-button"
        txn={{ ...props.txForm, attachments }}
        onChange={documentsChange}
        onSizeChange={props.onSizeChange}
        lastTabName={lastTab === 'addDoc' ? props.lastTabName : null}
      />
      {/* **** TRANSFER **** */}
      {hasFoundMatchingTransfer && isSupportedTransferTxn(props.txForm) && props.goToTransferAction && isTransferAccountPresent &&
        <div className={classNames(classes.detailsRow, classes.txfrRow)}>
          <div className={classes.rowIcon}>
            <TransferIcon fontSize="small" />
          </div>
          {/* @ts-expect-error: missing props */}
          <ButtonBase
            focusRipple
            label="go to transfer"
            className={classes.link}
            onClick={() => props.goToTransferAction && props.goToTransferAction()}
            name={lastTab === 'transfer' ? props.lastTabName : null}
          >
            <Typography
              className={classes.link}
            >
              {'Go to the other side of this transfer '}
            </Typography>
          </ButtonBase>
        </div>}

      {showTxnJson &&
        <div className={classNames(classes.detailsRow, classes.txfrRow)}>
          <div className={classes.rowIcon}>
            <TransferIcon fontSize="small" />
          </div>
          {/* @ts-expect-error: missing props */}
          <ButtonBase
            focusRipple
            label="json view"
            className={classes.link}
            onClick={() => dialogAlert(
              'JSON View',
              JSON.stringify(props.txForm),
              null,
              ['Close'],
              'info',
            )}
            name={lastTab === 'transfer' ? props.lastTabName : null}
          >
            <Typography
              className={classes.link}
            >
              Display Transaction JSON
            </Typography>
          </ButtonBase>
        </div>}
    </Paper>
  );
};

type DetailsPanelUIStateType = {
  newScheduledTransactionClientId?: string;
  deleteScheduledTransactionId?: string;
  recurrencePayee?: string;
  recurrenceFrequency?: string;
};

const DetailsPanelUIState = ImmutableRecord<DetailsPanelUIStateType>({
  newScheduledTransactionClientId: undefined,
  deleteScheduledTransactionId: undefined,
  recurrencePayee: undefined,
  recurrenceFrequency: undefined,
});

const uiStateConfig = {
  name: (props) => `DetailsPanel:${props.txForm.id}`,
  state: () => DetailsPanelUIState(),
  persist: true,
};

(DetailsPanel as any).whyDidYouRender = true;

export default compose(
  UIState(uiStateConfig),
)(DetailsPanel);
